import { computed, inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { patchState, signalStoreFeature, type, withComputed, withMethods } from '@ngrx/signals'
import {
  entityConfig,
  removeAllEntities,
  removeEntity,
  setEntities,
  setEntity,
  updateEntity,
  upsertEntity,
  withEntities,
} from '@ngrx/signals/entities'
import { Events, withEventHandlers } from '@ngrx/signals/events'
import { delayWhen, filter, map, tap } from 'rxjs'

import { asc } from '@app/core/array/compare'
import { longTimer } from '@app/core/rxjs/long-timer'

import { conversationEvents } from './conversation.events'
import { findNextMessageToExpire, getExpiresAt, isMessageExpired, toExpiredEntity } from './message.feature.utils'
import { realtimeMessagingEvents } from './realtime-messaging.events'
import { sendingEvents } from './sending.events'
import type { Message } from '../../models/message.model'
import { boostEvents } from '../../modules/boost/store/boost.events'
import { messagePhotoEvents } from '../../modules/message-photo/components/message-photo/message-photo.events'
import { voiceMessageEvents } from '../../modules/voice-message/components/voice-message/voice-message.events'

const collection = '_message'

const config = entityConfig({
  entity: type<Message>(),
  collection,
  selectId: message => message.timestamp,
})

export const messageFeature = () =>
  signalStoreFeature(
    withEntities(config),
    withComputed(store => ({
      messages: computed(() => store._messageEntities().sort(asc(x => x.fe_message_id ?? x.timestamp))),
    })),
    withMethods(store => ({
      _updateEntity: (
        id: Message['timestamp'],
        changes: Partial<Message> | ((entity: Message) => Partial<Message>),
      ) => {
        patchState(store, updateEntity({ id, changes }, config))
      },
      _setEntities: (messages: Message[]) => patchState(store, setEntities(messages, config)),
      _setEntity: (message: Message) => patchState(store, setEntity(message, config)),
      _removeAllEntities: () => patchState(store, removeAllEntities(config)),
      _removeEntity: (messageTimestamp: Message['timestamp']) =>
        patchState(store, removeEntity(messageTimestamp, config)),
      _upsertEntity: (message: Message) => patchState(store, upsertEntity(message, config)),
    })),
    withMethods(store => ({
      _setMessageAndRemoveFake: (message: Message) => {
        const existingFakeMessage = message.fe_message_id ? store._messageEntityMap()[message.fe_message_id] : undefined
        if (existingFakeMessage) {
          store._removeEntity(existingFakeMessage.timestamp)
          store._setEntity(message)
        } else {
          store._upsertEntity(message)
        }
      },
    })),
    withEventHandlers((store, events = inject(Events)) => ({
      onConversationSelect: events.on(conversationEvents.select).pipe(
        map(({ payload }) => payload),
        tap(() => store._removeAllEntities()),
      ),

      onConversationLoadSuccess$: events.on(conversationEvents.loadSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ response }) => store._setEntities(response.messages)),
      ),

      onLoadMoreMessagesSuccess$: events.on(conversationEvents.loadMoreMessagesSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ response }) => store._setEntities(response.messages)),
      ),

      onUnlockConversationSuccess$: events.on(conversationEvents.unlockSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ response }) => store._setEntities(response.messages)),
      ),

      onSendFakeMessageSuccess$: events.on(sendingEvents.sendFakeMessageSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ fakeMessages }) => store._setEntities(fakeMessages)),
      ),

      onSendRealMessageSuccess$: events.on(sendingEvents.sendRealMessageSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ response }) => response.messages.forEach(message => store._setMessageAndRemoveFake(message))),
      ),

      onSendMessageFailure$: events.on(sendingEvents.sendMessageFailure).pipe(
        map(({ payload }) => payload),
        tap(({ payload }) => payload.messages.forEach(message => store._removeEntity(message.fe_message_id))),
      ),

      onBoostMessage$: events.on(boostEvents.boost).pipe(
        map(({ payload }) => payload),
        tap(({ message }) => store._updateEntity(message.timestamp, { boostInProgress: true })),
      ),

      onBoostMessageEnd$: events.on(boostEvents.boostEnd).pipe(
        map(({ payload }) => payload),
        tap(({ messageTimestamp }) => store._updateEntity(messageTimestamp, { boostInProgress: false })),
      ),

      onBoostMessageSuccess$: events.on(boostEvents.boostSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ message }) => store._updateEntity(message.timestamp, { boosted: true })),
      ),

      onRealtimeMessageUpsert$: events.on(realtimeMessagingEvents.messageUpsert).pipe(
        map(({ payload }) => payload),
        tap(({ message }) => store._setMessageAndRemoveFake(message)),
      ),

      onMessagePhotoView$: events.on(messagePhotoEvents.view).pipe(
        map(({ payload }) => payload),
        filter(({ message }) => !message.is_me),
        tap(({ message }) => store._updateEntity(message.timestamp, { status: 'viewed' })),
      ),

      onMessageUnlock$: events.on(messagePhotoEvents.unlock, voiceMessageEvents.unlock).pipe(
        map(({ payload }) => payload),
        filter(({ message }) => !isMessageExpired(message)),
        tap(({ message }) => store._updateEntity(message.timestamp, { unlockInProgress: true })),
      ),

      onMessageUnlockEnd$: events.on(messagePhotoEvents.unlockEnd, voiceMessageEvents.unlockEnd).pipe(
        map(({ payload }) => payload),
        tap(({ messageTimestamp }) => store._updateEntity(messageTimestamp, { unlockInProgress: false })),
      ),

      onMessageUnlockSuccess$: events.on(messagePhotoEvents.unlockSuccess, voiceMessageEvents.unlockSuccess).pipe(
        map(({ payload }) => payload),
        tap(({ response }) => store._setEntity(response.message)),
      ),

      onMessageExpire$: toObservable(store.messages).pipe(
        map(findNextMessageToExpire),
        filter(Boolean),
        delayWhen(message => longTimer(new Date(getExpiresAt(message)))),
        tap(message => store._setEntity(toExpiredEntity(message))),
      ),
    })),
  )
